'use client'

import Link from 'next/link'
import { useState, useEffect, useRef } from 'react'
import { useCart } from '@/contexts/CartContext'
import { formatPrice, pluralPl } from '@/lib/format'
import { thumbUrl } from '@/lib/image-url'
import { Spin360Viewer } from './Spin360Viewer'
import { Reviews, type ReviewItem } from './Reviews'
import { CHEAPEST_SHIPPING, FREE_SHIPPING_THRESHOLD } from '@/lib/shipping'
import { mergeProductSpecAttributes, type SpecAttribute } from '@/lib/product-spec-attributes'

interface Variant {
  id: string
  name: string
  price: string | null
  stock: number
  attributes?: SpecAttribute[]
}

interface ProductDetailsProps {
  product: {
    id: string
    name: string
    slug: string
    description: string | null
    specs?: string | null
    attributes?: SpecAttribute[]
    basePrice: string
    spin360ModelId?: string | null
    spin360Frames?: number | null
    brand: {
      name: string
      slug: string
      logoUrl?: string | null
      manufacturerName?: string | null
      manufacturerAddress?: string | null
      manufacturerEmail?: string | null
      euRepName?: string | null
      euRepAddress?: string | null
      euRepEmail?: string | null
    } | null
    category: { name: string; slug: string; parent?: { slug: string } | null } | null
    images: { url: string; alt: string | null }[]
    variants: Variant[]
  }
  reviews: ReviewItem[]
  reviewStats: { count: number; avg: number }
  reviewsUnavailable?: boolean
  categorySlug: string
}

// Opisy z panelu (TipTap) i wzbogacane z diveinn są HTML; starsze opisy to plain text.
// Detekcja tagów → HTML renderujemy bezpiecznie (treść tylko od admina/zaufanego źródła).
function renderRichOrText(content: string | null | undefined) {
  if (!content) return null
  return /<\/?[a-z][\s\S]*>/i.test(content) ? (
    <div
      className="rich-content leading-relaxed"
      style={{ color: 'rgba(255,255,255,0.7)' }}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  ) : (
    <p className="leading-relaxed whitespace-pre-line" style={{ color: 'rgba(255,255,255,0.7)' }}>
      {content}
    </p>
  )
}

function SpecAttributesTable({ rows }: { rows: SpecAttribute[] }) {
  return (
    <table
      className="w-full text-sm"
      style={{ borderCollapse: 'collapse', color: 'rgba(255,255,255,0.75)' }}
    >
      <caption className="sr-only">Specyfikacja techniczna produktu</caption>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.key}-${row.value}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <th
              scope="row"
              className="py-3 pr-6 text-left font-medium align-top"
              style={{ color: 'rgba(255,255,255,0.55)', width: '40%' }}
            >
              {row.key}
            </th>
            <td className="py-3 align-top" style={{ color: 'rgba(255,255,255,0.85)' }}>
              {row.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function ProductDetails({ product, reviews, reviewStats, reviewsUnavailable, categorySlug }: ProductDetailsProps) {
  // Domyślnie pierwszy wariant Z DOSTĘPNYM STANEM — wybór wariantu ze stock=0
  // startował przycisk „Dodaj" jako disabled, mimo że inne warianty są dostępne.
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(
    product.variants.find((v) => v.stock > 0) ?? product.variants[0] ?? null
  )
  const [activeImage, setActiveImage] = useState(0)
  const [imgVisible, setImgVisible] = useState(true)
  const [show360, setShow360] = useState(false)
  const has360 = Boolean(product.spin360ModelId && product.spin360Frames)
  const [quantity, setQuantity] = useState(1)
  const [added, setAdded] = useState(false)
  const [priceAnimating, setPriceAnimating] = useState(false)
  const [stickyVisible, setStickyVisible] = useState(false)
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const addBtnRef = useRef<HTMLButtonElement>(null)

  // Lightbox (pełnoekranowy podgląd zdjęcia + zoom rolką myszy / pinch na telefonie)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null)
  const pinchRef = useRef<{ dist: number; baseZoom: number } | null>(null)
  const lbImgRef = useRef<HTMLImageElement>(null)
  const MIN_ZOOM = 1
  const MAX_ZOOM = 5

  useEffect(() => {
    // Detekcja urządzenia dotykowego po zamontowaniu (uniknięcie mismatchu hydracji).
    // eslint-disable-next-line react-hooks/set-state-in-effect -- jednorazowa detekcja on-mount
    setIsTouch(window.matchMedia('(hover: none)').matches)
  }, [])

  useEffect(() => {
    const el = addBtnRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setStickyVisible(!entry.isIntersecting),
      { threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const { addItem } = useCart()

  function handleVariantChange(v: Variant) {
    setPriceAnimating(true)
    setTimeout(() => {
      setSelectedVariant(v)
      setQuantity(1)
      setPriceAnimating(false)
    }, 150)
  }

  const price = selectedVariant?.price ?? product.basePrice

  function handleAddToCart() {
    if (!selectedVariant) return
    addItem({
      productId: product.id,
      variantId: selectedVariant.id,
      name: product.name,
      variantName: selectedVariant.name,
      price: Number(selectedVariant.price ?? product.basePrice),
      quantity,
      image: product.images[0]?.url,
      slug: product.slug,
      categorySlug: product.category?.parent?.slug ?? product.category?.slug ?? 'katalog',
    })
    setAdded(true)
    setTimeout(() => setAdded(false), 2000)
  }

  // Zmiana zdjęcia z tym samym fade'em co miniatury (used przez miniatury i strzałki).
  function changeImage(i: number) {
    setShow360(false)
    if (i === activeImage) return
    setImgVisible(false)
    setTimeout(() => { setActiveImage(i); setImgVisible(true) }, 120)
  }
  const imgCount = product.images.length
  const goPrev = () => changeImage((activeImage - 1 + imgCount) % imgCount)
  const goNext = () => changeImage((activeImage + 1) % imgCount)

  // --- Lightbox ---
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }) }
  const openLightbox = () => { if (product.images.length) { resetZoom(); setLightboxOpen(true) } }
  const closeLightbox = () => { setLightboxOpen(false); resetZoom() }
  // Nawigacja w lightboxie — natychmiast (bez fade'u miniatur) + reset zoomu
  const lbGo = (i: number) => { setActiveImage((i + imgCount) % imgCount); resetZoom() }

  // Blokada scrolla tła + obsługa klawiatury gdy lightbox otwarty
  useEffect(() => {
    if (!lightboxOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox()
      else if (e.key === 'ArrowLeft') lbGo(activeImage - 1)
      else if (e.key === 'ArrowRight') lbGo(activeImage + 1)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen, activeImage, imgCount])

  // Zoom rolką myszy. Natywny listener (non-passive) — żeby preventDefault zadziałał
  // i strona pod spodem się nie przewijała.
  useEffect(() => {
    if (!lightboxOpen) return
    const el = lbImgRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      setZoom((z) => {
        const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z - e.deltaY * 0.0015 * z))
        if (next <= MIN_ZOOM) setPan({ x: 0, y: 0 })
        return next
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [lightboxOpen, activeImage])

  // Przeciąganie zdjęcia gdy przybliżone
  const onDragStart = (clientX: number, clientY: number) => {
    if (zoom <= MIN_ZOOM) return
    dragRef.current = { startX: clientX, startY: clientY, baseX: pan.x, baseY: pan.y }
    setDragging(true)
  }
  const onDragMove = (clientX: number, clientY: number) => {
    const d = dragRef.current
    if (!d) return
    setPan({ x: d.baseX + (clientX - d.startX), y: d.baseY + (clientY - d.startY) })
  }
  const onDragEnd = () => { dragRef.current = null; setDragging(false) }

  // Dotyk: pinch (2 palce) = zoom, 1 palec = przesuwanie gdy przybliżone
  const touchDist = (t: React.TouchList) => {
    const dx = t[0].clientX - t[1].clientX
    const dy = t[0].clientY - t[1].clientY
    return Math.hypot(dx, dy)
  }
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      pinchRef.current = { dist: touchDist(e.touches), baseZoom: zoom }
    } else if (e.touches.length === 1) {
      onDragStart(e.touches[0].clientX, e.touches[0].clientY)
    }
  }
  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const ratio = touchDist(e.touches) / pinchRef.current.dist
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, pinchRef.current.baseZoom * ratio))
      setZoom(next)
      if (next <= MIN_ZOOM) setPan({ x: 0, y: 0 })
    } else if (e.touches.length === 1) {
      onDragMove(e.touches[0].clientX, e.touches[0].clientY)
    }
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) pinchRef.current = null
    if (e.touches.length === 0) onDragEnd()
  }

  // Parent + currently selected variant; variant overrides same normalized key.
  const structuredSpecs = mergeProductSpecAttributes(
    product.attributes,
    selectedVariant?.attributes,
  )
  // Zakładki sekcji szczegółów: Opis / Specyfikacja (jeśli są) + zawsze Opinie.
  const tabs: { key: string; label: string }[] = []
  if (product.description) tabs.push({ key: 'opis', label: 'Opis' })
  if (structuredSpecs.length > 0 || product.specs) tabs.push({ key: 'spec', label: 'Specyfikacja' })
  tabs.push({ key: 'opinie', label: reviewStats.count > 0 ? `Opinie (${reviewStats.count})` : 'Opinie' })
  const activeKey = activeTab && tabs.some((t) => t.key === activeTab) ? activeTab : tabs[0]?.key

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      {/* Galeria */}
      <div>
        <div
          className="group relative aspect-[4/3] rounded-2xl overflow-hidden mb-4"
          style={{ backgroundColor: '#0D1E30' }}
          tabIndex={imgCount > 1 ? 0 : -1}
          role={imgCount > 1 ? 'group' : undefined}
          aria-label={imgCount > 1 ? `Galeria zdjęć produktu, zdjęcie ${activeImage + 1} z ${imgCount}` : undefined}
          onKeyDown={(e) => {
            if (imgCount < 2) return
            if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev() }
            else if (e.key === 'ArrowRight') { e.preventDefault(); goNext() }
          }}
        >
          {show360 && has360 ? (
            <Spin360Viewer
              modelId={product.spin360ModelId!}
              frames={product.spin360Frames!}
              alt={product.name}
            />
          ) : product.images[activeImage] ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={product.images[activeImage].url}
              alt={product.images[activeImage].alt ?? product.name}
              width={800}
              height={600}
              fetchPriority="high"
              decoding="async"
              onClick={openLightbox}
              className="gallery-main-img w-full h-full object-cover"
              style={{
                opacity: imgVisible ? 1 : 0,
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
              brak zdjęcia
            </div>
          )}
          {has360 && (
            <button
              type="button"
              onClick={() => setShow360((v) => !v)}
              aria-pressed={show360}
              aria-label={show360 ? 'Wróć do zdjęć' : 'Obejrzyj w 360 stopni'}
              className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-transform hover:scale-105"
              style={{
                backgroundColor: show360 ? '#FF6B47' : 'rgba(3,8,15,0.7)',
                color: show360 ? '#03080F' : 'rgba(255,255,255,0.9)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" />
              </svg>
              360°
            </button>
          )}
          {!show360 && imgCount > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Poprzednie zdjęcie"
                className="gallery-arrow gallery-arrow-prev"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Następne zdjęcie"
                className="gallery-arrow gallery-arrow-next"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <div className="gallery-counter">{activeImage + 1} / {imgCount}</div>
            </>
          )}
          {!show360 && product.images[activeImage] && (
            <button
              type="button"
              onClick={openLightbox}
              aria-label="Powiększ zdjęcie na pełny ekran"
              className="gallery-zoom-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </button>
          )}
        </div>
        {product.images.length > 1 && (
          <div className="flex gap-2">
            {product.images.map((img, i) => (
              <button
                key={i}
                onClick={() => changeImage(i)}
                aria-label={`Wyświetl zdjęcie ${i + 1} z ${product.images.length}`}
                aria-pressed={i === activeImage}
                className="w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors"
                style={{
                  borderColor: i === activeImage ? '#FF6B47' : 'rgba(255,255,255,0.15)',
                  transition: 'border-color 0.2s ease',
                }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={thumbUrl(img.url)} alt={img.alt ?? `Zdjęcie produktu ${i + 1}`} width={64} height={64} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div>
        {product.brand && (
          product.brand.logoUrl ? (
            <Link
              href={`/katalog/marki/${product.brand.slug}`}
              aria-label={`Marka ${product.brand.name} — zobacz wszystkie produkty`}
              className="inline-flex items-center mb-3 rounded-lg px-3 py-1.5 transition-transform hover:scale-105"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={product.brand.logoUrl}
                alt={`Logo ${product.brand.name}`}
                loading="lazy"
                decoding="async"
                width={144}
                height={24}
                className="h-6 w-auto object-contain"
                style={{ maxWidth: '9rem' }}
              />
            </Link>
          ) : (
            <Link
              href={`/katalog/marki/${product.brand.slug}`}
              className="inline-block text-sm font-bold uppercase tracking-wider mb-2 hover:underline"
              style={{ color: '#1FB8B0' }}
            >
              {product.brand.name}
            </Link>
          )
        )}
        <h1 className="font-heading text-3xl font-bold text-white mb-2">{product.name}</h1>

        {/* Dowód społeczny nad foldem: ocena + liczba opinii z anchor-linkiem do zakładki
            (wzorzec Amazon/Zalando — opinie schowane tylko w zakładce nie pracowały). */}
        {reviewStats.count > 0 && (
          <button
            type="button"
            onClick={() => {
              setActiveTab('opinie')
              document.getElementById('szczegoly-produktu')?.scrollIntoView({ behavior: 'smooth' })
            }}
            className="flex items-center gap-2 mb-4 group"
            aria-label={`Ocena ${reviewStats.avg.toFixed(1)} na 5, ${reviewStats.count} ${pluralPl(reviewStats.count, ['opinia', 'opinie', 'opinii'])} — przejdź do opinii`}
          >
            <span className="flex" aria-hidden="true">
              {[1, 2, 3, 4, 5].map((i) => (
                <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill={i <= Math.round(reviewStats.avg) ? '#FFB020' : 'rgba(255,255,255,0.18)'}>
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ))}
            </span>
            <span className="text-sm group-hover:underline" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {reviewStats.avg.toFixed(1)} · {reviewStats.count} {pluralPl(reviewStats.count, ['opinia', 'opinie', 'opinii'])}
            </span>
          </button>
        )}

        <p
          className="text-3xl font-bold text-white mb-6"
          style={{
            transition: 'opacity 0.15s ease, transform 0.15s ease',
            opacity: priceAnimating ? 0 : 1,
            transform: priceAnimating ? 'translateY(-4px)' : 'translateY(0)',
          }}
        >
          {formatPrice(price)}
        </p>

        {product.variants.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-medium mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>Wariant</p>
            <div className="flex flex-wrap gap-2">
              {product.variants.map((v) => (
                <button
                  key={v.id}
                  onClick={() => handleVariantChange(v)}
                  disabled={v.stock === 0}
                  aria-pressed={selectedVariant?.id === v.id}
                  aria-label={`${v.name} — wariant${v.stock === 0 ? ' niedostępny' : ''}`}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium border transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  style={selectedVariant?.id === v.id
                    ? { backgroundColor: '#FF6B47', color: '#03080F', borderColor: '#FF6B47', transition: 'all 0.2s ease' }
                    : { backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)', transition: 'all 0.2s ease' }}
                >
                  {v.name}
                  {v.stock === 0 && ' (brak)'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Stock status — aria-live ogłasza zmianę po wyborze innego wariantu */}
        {selectedVariant && (
          <p
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="text-sm mb-4 flex items-center gap-1.5"
          >
            {selectedVariant.stock === 0 ? (
              <span style={{ color: '#F87171' }}>
                <span aria-hidden="true">● </span>Brak w magazynie
              </span>
            ) : selectedVariant.stock <= 3 ? (
              <span style={{ color: '#F59E0B' }}>
                <span aria-hidden="true">● </span>
                {selectedVariant.stock === 1
                  ? 'Ostatnia sztuka'
                  : `Ostatnie ${selectedVariant.stock} ${pluralPl(selectedVariant.stock, ['sztuka', 'sztuki', 'sztuk'])}`}
              </span>
            ) : (
              <span style={{ color: '#34D399' }}>
                <span aria-hidden="true">● </span>Dostępny
              </span>
            )}
          </p>
        )}

        {/* Quantity */}
        {selectedVariant && selectedVariant.stock > 0 && (
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>Ilość</span>
            <div className="flex items-center rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.15)' }}>
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                aria-label="Zmniejsz ilość"
                className="w-10 h-10 flex items-center justify-center text-lg font-medium transition-colors hover:bg-white/5"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                −
              </button>
              <span
                className="w-10 text-center text-sm font-bold"
                style={{ color: 'white' }}
                aria-live="polite"
                aria-label={`Ilość: ${quantity}`}
              >
                {quantity}
              </span>
              <button
                onClick={() => setQuantity((q) => Math.min(selectedVariant.stock, q + 1))}
                aria-label="Zwiększ ilość"
                className="w-10 h-10 flex items-center justify-center text-lg font-medium transition-colors hover:bg-white/5"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                +
              </button>
            </div>
          </div>
        )}

        <button
          ref={addBtnRef}
          onClick={handleAddToCart}
          disabled={!selectedVariant || selectedVariant.stock === 0}
          aria-label={added ? 'Dodano do koszyka — produkt dodany pomyślnie' : `Dodaj do koszyka${selectedVariant ? ` — wybrany wariant: ${selectedVariant.name}` : ''}`}
          className="w-full py-4 rounded-2xl font-semibold text-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed mb-3"
          style={{
            backgroundColor: added ? '#1FB8B0' : '#FF6B47',
            color: '#03080F',
          }}
        >
          {added ? '✓ Dodano do koszyka' : 'Dodaj do koszyka'}
        </button>

        <div aria-live="polite" aria-atomic="true">
          {added && (
            <Link
              href="/koszyk"
              className="block text-center text-sm font-medium hover:underline mb-3"
              style={{ color: '#1FB8B0' }}
            >
              Zobacz koszyk →
            </Link>
          )}
        </div>

        {/* Koszt/czas dostawy przy decyzji zakupowej (Baymard: nieznany koszt dostawy
            = powód nr 1 porzucenia koszyka). Wartości z lib/shipping przez SITE_FACTS. */}
        <p className="text-center text-xs mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
          Wysyłka w 24h (dni robocze) · Dostawa od {CHEAPEST_SHIPPING} zł · Darmowa od {FREE_SHIPPING_THRESHOLD} zł
        </p>

        {/* Trust badges */}
        <div
          className="grid grid-cols-3 gap-3 mt-4 py-5"
          style={{ borderTop: '1px solid rgba(255,255,255,0.07)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          {[
            {
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7"/><path d="M3 7h18"/><path d="M8 11h2"/><path d="M8 15h4"/>
                </svg>
              ),
              text: 'Przelew bankowy',
            },
            {
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>
                </svg>
              ),
              text: `Darmowa dostawa od ${FREE_SHIPPING_THRESHOLD} zł`,
            },
            {
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
                </svg>
              ),
              text: '14 dni na zwrot',
            },
          ].map(({ icon, text }) => (
            <div key={text} className="flex flex-col items-center gap-2 text-center">
              <span
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(31,184,176,0.10)', color: '#1FB8B0' }}
              >
                {icon}
              </span>
              <span className="text-xs leading-snug" style={{ color: 'rgba(255,255,255,0.65)' }}>{text}</span>
            </div>
          ))}
        </div>

      </div>
    </div>

    {/* Szczegóły produktu — pełna szerokość pod galerią i boxem zakupu */}
    <div className="mt-12 space-y-10">
      {tabs.length > 0 && (
        <section id="szczegoly-produktu" className="pt-10" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {tabs.length > 1 ? (
            <div
              role="tablist"
              aria-label="Opis, specyfikacja i opinie o produkcie"
              className="flex flex-wrap gap-6 mb-6"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
            >
              {tabs.map((t) => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={activeKey === t.key}
                  onClick={() => setActiveTab(t.key)}
                  className="font-heading text-xl font-bold pb-3 -mb-px border-b-2 transition-colors"
                  style={
                    activeKey === t.key
                      ? { color: '#FFFFFF', borderColor: '#FF6B47' }
                      : { color: 'rgba(255,255,255,0.45)', borderColor: 'transparent' }
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          ) : (
            <h2 className="font-heading text-2xl font-bold text-white mb-4">{tabs[0].label}</h2>
          )}

          <div role="tabpanel">
            {activeKey === 'opis' && renderRichOrText(product.description)}
            {activeKey === 'spec' && (
              structuredSpecs.length > 0
                ? <SpecAttributesTable rows={structuredSpecs} />
                : renderRichOrText(product.specs)
            )}
            {activeKey === 'opinie' && (
              <Reviews
                productId={product.id}
                categorySlug={categorySlug}
                slug={product.slug}
                reviews={reviews}
                stats={reviewStats}
                unavailable={reviewsUnavailable}
              />
            )}
          </div>
        </section>
      )}

      {product.brand?.manufacturerName && (
        <section className="pt-10" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <h2 className="font-heading font-semibold text-white mb-1">Producent</h2>
          <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Informacje wymagane rozporządzeniem UE 2023/988 (GPSR).
          </p>
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
            <div className="rounded-xl p-4" style={{ backgroundColor: '#0D1E30', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#1FB8B0' }}>Producent</p>
              <p className="text-sm font-medium text-white">{product.brand.manufacturerName}</p>
              {product.brand.manufacturerAddress && (
                <p className="text-sm mt-1 whitespace-pre-line" style={{ color: 'rgba(255,255,255,0.6)' }}>{product.brand.manufacturerAddress}</p>
              )}
              {product.brand.manufacturerEmail && (
                <a href={`mailto:${product.brand.manufacturerEmail}`} className="text-sm mt-1 inline-block hover:underline" style={{ color: '#1FB8B0' }}>
                  {product.brand.manufacturerEmail}
                </a>
              )}
            </div>
            {product.brand.euRepName && (
              <div className="rounded-xl p-4" style={{ backgroundColor: '#0D1E30', border: '1px solid rgba(255,255,255,0.07)' }}>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#1FB8B0' }}>Osoba odpowiedzialna w UE</p>
                <p className="text-sm font-medium text-white">{product.brand.euRepName}</p>
                {product.brand.euRepAddress && (
                  <p className="text-sm mt-1 whitespace-pre-line" style={{ color: 'rgba(255,255,255,0.6)' }}>{product.brand.euRepAddress}</p>
                )}
                {product.brand.euRepEmail && (
                  <a href={`mailto:${product.brand.euRepEmail}`} className="text-sm mt-1 inline-block hover:underline" style={{ color: '#1FB8B0' }}>
                    {product.brand.euRepEmail}
                  </a>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      <div className="pt-8 flex items-center gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.69h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l.91-1.38a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 17z" />
        </svg>
        <span className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Pytania o produkt?</span>
        <a
          href={`mailto:kontakt@divemate.pl?subject=${encodeURIComponent(`Pytanie o produkt: ${product.name}`)}`}
          className="text-sm font-medium hover:underline"
          style={{ color: '#1FB8B0' }}
        >
          Napisz do nas
        </a>
      </div>
    </div>
    {/* Sticky mobile add-to-cart bar */}
    {selectedVariant && selectedVariant.stock > 0 && (
      <div
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden"
        style={{
          backgroundColor: '#0D1E30',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          padding: '12px 16px',
          transform: stickyVisible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.2s ease',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.4)',
        }}
      >
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>{product.name}</p>
            <p className="text-base font-bold" style={{ color: 'white' }}>{formatPrice(price)}</p>
          </div>
          <button
            onClick={handleAddToCart}
            disabled={!selectedVariant || selectedVariant.stock === 0}
            aria-label={added ? 'Dodano' : 'Dodaj do koszyka'}
            className="flex-shrink-0 px-6 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-40"
            style={{ backgroundColor: added ? '#1FB8B0' : '#FF6B47', color: '#03080F', transition: 'background-color 0.2s ease' }}
          >
            {added ? '✓ Dodano' : 'Dodaj do koszyka'}
          </button>
        </div>
      </div>
    )}

    {/* Lightbox — pełnoekranowy podgląd zdjęcia z zoomem (rolka myszy) */}
    {lightboxOpen && product.images[activeImage] && (
      <div
        className="lightbox-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Powiększone zdjęcie produktu"
        onClick={closeLightbox}
      >
        <button type="button" onClick={closeLightbox} aria-label="Zamknij" className="lightbox-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="lightbox-stage" onClick={(e) => e.stopPropagation()}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            ref={lbImgRef}
            src={product.images[activeImage].url}
            alt={product.images[activeImage].alt ?? product.name}
            draggable={false}
            onClick={() => { if (zoom <= MIN_ZOOM) setZoom(2) }}
            onDoubleClick={resetZoom}
            onMouseDown={(e) => onDragStart(e.clientX, e.clientY)}
            onMouseMove={(e) => onDragMove(e.clientX, e.clientY)}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className="lightbox-img"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              cursor: zoom > MIN_ZOOM ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
              transition: dragging ? 'none' : 'transform 0.15s ease',
            }}
          />
        </div>

        {imgCount > 1 && (
          <>
            <button type="button" onClick={(e) => { e.stopPropagation(); lbGo(activeImage - 1) }} aria-label="Poprzednie zdjęcie" className="lightbox-arrow lightbox-arrow-prev">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); lbGo(activeImage + 1) }} aria-label="Następne zdjęcie" className="lightbox-arrow lightbox-arrow-next">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </>
        )}

        <div className="lightbox-hint">{activeImage + 1} / {imgCount} · {isTouch ? 'zsuń palce, aby przybliżyć' : 'przewiń, aby przybliżyć'}</div>

        {imgCount > 1 && (
          <div className="lightbox-thumbs" onClick={(e) => e.stopPropagation()}>
            {product.images.map((img, i) => (
              <button
                key={i}
                onClick={() => lbGo(i)}
                aria-label={`Pokaż zdjęcie ${i + 1}`}
                aria-pressed={i === activeImage}
                className="lightbox-thumb"
                style={{ borderColor: i === activeImage ? '#FF6B47' : 'transparent', opacity: i === activeImage ? 1 : 0.5 }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={thumbUrl(img.url)} alt={img.alt ?? `Miniatura ${i + 1}`} width={56} height={56} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </button>
            ))}
          </div>
        )}
      </div>
    )}
    </>
  )
}
